import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TeacherOdooQuestMonitor({ teacherEmail, questId, questTitle, onClose }) {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContainers();
    const interval = setInterval(fetchContainers, 30000);
    return () => clearInterval(interval);
  }, [teacherEmail, questId]);

  const fetchContainers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BACKEND_URL}/api/teacher/${teacherEmail}/odoo-containers`
      );
      
      // Filtrar solo los contenedores de este quest específico
      const allContainers = response.data.containers || [];
      const questContainers = allContainers.filter(
        c => c.quest_id === questId
      );
      
      setContainers(questContainers);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching containers:', err);
      setError("Error al cargar contenedores");
      setLoading(false);
    }
  };

  const openOdoo = (port) => {
    const url = `https://odoo-${port}.play.leaderdreams.com/web`;
    window.open(url, '_blank');
  };

  const copyLink = (port) => {
    const url = `https://odoo-${port}.play.leaderdreams.com/web`;
    navigator.clipboard.writeText(url);
    alert('Enlace copiado al portapapeles');
  };

  const refreshContainers = () => {
    fetchContainers();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-white">
              🏢 Contenedores Odoo
            </h2>
            <p className="text-purple-200 text-sm mt-1">
              {questTitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-purple-200 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-gray-600">Cargando contenedores...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchContainers}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Reintentar
              </button>
            </div>
          ) : containers.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <div className="text-6xl mb-4">📭</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                No hay contenedores activos
              </h3>
              <p className="text-gray-500">
                Los alumnos aún no han iniciado sus instancias de Odoo para este ejercicio.
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Los contenedores aparecerán cuando los alumnos hagan clic en "Abrir Odoo"
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-gray-600">
                  Total de contenedores activos: <strong>{containers.length}</strong>
                </p>
                <button
                  onClick={refreshContainers}
                  className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 flex items-center gap-2"
                >
                  <span>🔄</span>
                  <span>Actualizar</span>
                </button>
              </div>

              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-left">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-sm font-semibold text-gray-700">Alumno</th>
                      <th className="px-4 py-3 text-sm font-semibold text-gray-700">Puerto</th>
                      <th className="px-4 py-3 text-sm font-semibold text-gray-700">Estado</th>
                      <th className="px-4 py-3 text-sm font-semibold text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {containers.map((container, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-bold text-sm">
                              {container.student_name?.charAt(0) || '?'}
                            </div>
                            <span className="font-medium text-gray-800">
                              {container.student_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">
                            {container.host_port}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            container.status === 'running' 
                              ? 'bg-green-100 text-green-800' 
                              : container.status === 'paused'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {container.status === 'running' ? '✅ Activo' : 
                             container.status === 'paused' ? '⏸️ Pausado' : '⏹️ Detenido'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openOdoo(container.host_port)}
                              className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 flex items-center gap-1"
                            >
                              <span>🏢</span>
                              <span>Abrir Odoo</span>
                            </button>
                            <button
                              onClick={() => copyLink(container.host_port)}
                              className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                              title="Copiar enlace"
                            >
                              📋
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-blue-800 mb-2">💡 Información útil</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Los contenedores se crean cuando el alumno hace clic en "Abrir Odoo"</li>
                  <li>• Cada alumno tiene su propia base de datos aislada</li>
                  <li>• Los contenedores se eliminan automáticamente después de un período de inactividad</li>
                  <li>• Usa el botón "Abrir Odoo" para acceder directamente a la instancia del alumno</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <p className="text-sm text-gray-500">
            Actualizado: {new Date().toLocaleTimeString()}
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default TeacherOdooQuestMonitor;
